import React, { useState, Suspense, lazy } from 'react';
import { UserProfile } from '../../types';
import {
    Footprints, ShoppingBag, Wrench, ArrowLeft, Utensils, Heart, Stethoscope,
    Camera, FileText, Wind, Calendar, Activity, CreditCard,
    Baby, Car, Lamp, AlertTriangle, Phone, Check, Circle, Plus,
    Trash2, MapPin, User, Bell, Sparkles, LucideIcon, Loader2, Droplets, Ruler, Users, Brain, Settings
} from 'lucide-react';

// Lazy load all tool components for better code splitting
const KickCounter = lazy(() => import('./KickCounter').then(m => ({ default: m.KickCounter })));
const HospitalBag = lazy(() => import('./HospitalBag').then(m => ({ default: m.HospitalBag })));
const FoodSafety = lazy(() => import('./FoodSafety').then(m => ({ default: m.FoodSafety })));
const NameMatcher = lazy(() => import('./NameMatcher').then(m => ({ default: m.NameMatcher })));
const DocVault = lazy(() => import('./DocVault').then(m => ({ default: m.DocVault })));
const BumpBooth = lazy(() => import('./BumpBooth').then(m => ({ default: m.BumpBooth })));
const BirthPlanBuilder = lazy(() => import('./BirthPlanBuilder').then(m => ({ default: m.BirthPlanBuilder })));
const RelaxMode = lazy(() => import('./RelaxMode').then(m => ({ default: m.RelaxMode })));
const HealthLog = lazy(() => import('./HealthLog').then(m => ({ default: m.HealthLog })));
const ContractionTimer = lazy(() => import('./ContractionTimer').then(m => ({ default: m.ContractionTimer })));
const DueDateCalculator = lazy(() => import('./DueDateCalculator').then(m => ({ default: m.DueDateCalculator })));
const BabySizeVisualizer = lazy(() => import('./BabySizeVisualizer').then(m => ({ default: m.BabySizeVisualizer })));
const Reminders = lazy(() => import('./Reminders').then(m => ({ default: m.Reminders })));
const FlashFactsViewer = lazy(() => import('./FlashFactsViewer').then(m => ({ default: m.FlashFactsViewer })));
const BloodPressureLog = lazy(() => import('./BloodPressureLog').then(m => ({ default: m.BloodPressureLog })));
const WaterTracker = lazy(() => import('./WaterTracker').then(m => ({ default: m.WaterTracker })));
const PelvicFloorTimer = lazy(() => import('./PelvicFloorTimer').then(m => ({ default: m.PelvicFloorTimer })));
const FundalHeightTracker = lazy(() => import('./FundalHeightTracker').then(m => ({ default: m.FundalHeightTracker })));
const PartnerHub = lazy(() => import('./PartnerHub').then(m => ({ default: m.PartnerHub })));
const MentalHealthScreener = lazy(() => import('./MentalHealthScreener').then(m => ({ default: m.MentalHealthScreener })));
const GlucoseTracker = lazy(() => import('./GlucoseTracker').then(m => ({ default: m.GlucoseTracker })));


// Loading component for lazy-loaded tools
const ToolLoader = () => (
    <div className="h-full flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
    </div>
);

// === REUSABLE TOOL CARD COMPONENT ===
interface ToolCardProps {
    icon: LucideIcon;
    iconColor: string; // e.g., "rose", "blue", "emerald"
    title: string;
    subtitle: string;
    onClick: () => void;
}


const ToolCard: React.FC<ToolCardProps> = ({ icon: Icon, iconColor, title, subtitle, onClick }) => {
    const colorMap: Record<string, { bg: string; text: string; shadow: string }> = {
        rose: { bg: 'bg-rose-100', text: 'text-rose-600', shadow: 'shadow-rose-500/20' },
        pink: { bg: 'bg-pink-100', text: 'text-pink-600', shadow: 'shadow-pink-500/20' },
        purple: { bg: 'bg-purple-100', text: 'text-purple-600', shadow: 'shadow-purple-500/20' },
        violet: { bg: 'bg-violet-100', text: 'text-violet-600', shadow: 'shadow-violet-500/20' },
        indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', shadow: 'shadow-indigo-500/20' },
        blue: { bg: 'bg-blue-100', text: 'text-blue-600', shadow: 'shadow-blue-500/20' },
        sky: { bg: 'bg-sky-100', text: 'text-sky-600', shadow: 'shadow-sky-500/20' },
        cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', shadow: 'shadow-cyan-500/20' },
        teal: { bg: 'bg-teal-100', text: 'text-teal-600', shadow: 'shadow-teal-500/20' },
        emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', shadow: 'shadow-emerald-500/20' },
        green: { bg: 'bg-green-100', text: 'text-green-600', shadow: 'shadow-green-500/20' },
        yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', shadow: 'shadow-yellow-500/20' },
        amber: { bg: 'bg-amber-100', text: 'text-amber-600', shadow: 'shadow-amber-500/20' },
        orange: { bg: 'bg-orange-100', text: 'text-orange-600', shadow: 'shadow-orange-500/20' },
        red: { bg: 'bg-red-100', text: 'text-red-600', shadow: 'shadow-red-500/20' },
    };

    const colors = colorMap[iconColor] || colorMap.blue;

    return (
        <button
            onClick={onClick}
            className="bg-white p-4 rounded-2xl shadow-sm border border-stone-100 text-left active:scale-[0.97] transition-all hover:shadow-md hover:border-stone-200 min-h-[120px] flex flex-col"
        >
            <div className={`${colors.bg} ${colors.text} w-10 h-10 rounded-xl flex items-center justify-center mb-2.5 flex-shrink-0`}>
                <Icon size={20} />
            </div>
            <h3 className="font-semibold text-stone-900 text-sm leading-tight">{title}</h3>
            <p className="text-stone-500 text-[11px] mt-1 leading-snug">{subtitle}</p>
        </button>
    );
};





// Section header component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
    <div className="col-span-2 mt-5 mb-1 first:mt-0">
        <span className="text-[11px] font-bold text-stone-500 uppercase tracking-wider">{title}</span>
    </div>
);


// === CAR SEAT CHECKLIST COMPONENT ===
const CAR_SEAT_ITEMS = [
    { id: '1', category: 'Before Installing', text: 'Read car seat manual completely', tip: 'Every seat is different' },
    { id: '2', category: 'Before Installing', text: 'Read vehicle owner manual (car seat section)', tip: 'Know your LATCH locations' },
    { id: '3', category: 'Before Installing', text: 'Register car seat with manufacturer', tip: 'Get recall notifications' },
    { id: '4', category: 'Before Installing', text: 'Check expiration date on car seat', tip: 'Usually 6-10 years from manufacture' },
    { id: '5', category: 'Before Installing', text: 'Remove any aftermarket additions', tip: 'Only use what came with seat' },
    { id: '6', category: 'Installation', text: 'Choose rear-facing position', tip: 'Safest is behind passenger seat' },
    { id: '7', category: 'Installation', text: 'Use LATCH OR seat belt, not both', tip: 'Unless manual says otherwise' },
    { id: '8', category: 'Installation', text: 'Route straps through correct slots', tip: 'Rear-facing = at or below shoulders' },
    { id: '9', category: 'Installation', text: 'Install at correct recline angle', tip: 'Use built-in level indicator' },
    { id: '10', category: 'Installation', text: 'Pull tight - less than 1 inch movement', tip: 'Grab at belt path, push hard' },
    { id: '11', category: 'Installation', text: 'Lock the seat belt if using', tip: 'May need locking clip' },
    { id: '12', category: 'Harness Setup', text: 'Harness at or below shoulders (rear-facing)', tip: 'Check after every growth spurt' },
    { id: '13', category: 'Harness Setup', text: 'Chest clip at armpit level', tip: 'Not on belly or neck' },
    { id: '14', category: 'Harness Setup', text: 'Pinch test - no slack in harness', tip: 'Cant pinch fabric at shoulder' },
    { id: '15', category: 'Harness Setup', text: 'Remove bulky coats before buckling', tip: 'Use blanket over straps instead' },
    { id: '16', category: 'Final Checks', text: 'Schedule free inspection', tip: 'Fire stations, hospitals, AAA' },
    { id: '17', category: 'Final Checks', text: 'Practice buckling with a doll', tip: 'Build muscle memory now' },
    { id: '18', category: 'Final Checks', text: 'Know when to move to next stage', tip: 'Max height/weight limits' },
];

const CarSeatChecklist: React.FC = () => {
    const [checked, setChecked] = React.useState<Set<string>>(() => {
        const saved = localStorage.getItem('coexpect_carseat');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });

    const toggle = (id: string) => {
        const next = new Set(checked);
        next.has(id) ? next.delete(id) : next.add(id);
        setChecked(next);
        localStorage.setItem('coexpect_carseat', JSON.stringify([...next]));
    };

    const categories = [...new Set(CAR_SEAT_ITEMS.map(i => i.category))];
    const progress = Math.round((checked.size / CAR_SEAT_ITEMS.length) * 100);

    return (
        <div className="h-full flex flex-col bg-stone-50 overflow-y-auto">
            <div className="bg-white px-5 py-5 border-b border-stone-100">
                <h1 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                    <Car className="text-green-600" /> Car Seat Install
                </h1>
                <div className="mt-3 bg-stone-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-green-500 h-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-stone-500 mt-1">{checked.size} of {CAR_SEAT_ITEMS.length} complete</p>
            </div>
            <div className="p-4 space-y-5 pb-24">
                {categories.map(cat => (
                    <div key={cat}>
                        <h2 className="text-xs font-bold text-stone-400 uppercase mb-2">{cat}</h2>
                        <div className="space-y-2">
                            {CAR_SEAT_ITEMS.filter(i => i.category === cat).map(item => (
                                <button key={item.id} onClick={() => toggle(item.id)} className={`w-full text-left p-3 rounded-xl border transition-all ${checked.has(item.id) ? 'bg-green-50 border-green-200' : 'bg-white border-stone-100'}`}>
                                    <div className="flex gap-3">
                                        <div className={`mt-0.5 ${checked.has(item.id) ? 'text-green-500' : 'text-stone-300'}`}>
                                            {checked.has(item.id) ? <Check size={18} /> : <Circle size={18} />}
                                        </div>
                                        <div>
                                            <p className={`font-medium text-sm ${checked.has(item.id) ? 'text-stone-500 line-through' : 'text-stone-800'}`}>{item.text}</p>
                                            <p className="text-xs text-stone-400 mt-0.5">💡 {item.tip}</p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// === NURSERY CHECKLIST COMPONENT ===
const NURSERY_ITEMS = [
    { id: '1', category: 'Sleep Essentials', text: 'Crib or bassinet', required: true },
    { id: '2', category: 'Sleep Essentials', text: 'Firm, flat mattress', required: true },
    { id: '3', category: 'Sleep Essentials', text: 'Fitted crib sheets (2-3)', required: true },
    { id: '4', category: 'Sleep Essentials', text: 'Waterproof mattress protector', required: false },
    { id: '5', category: 'Sleep Essentials', text: 'Baby monitor', required: false },
    { id: '6', category: 'Sleep Essentials', text: 'White noise machine', required: false },
    { id: '7', category: 'Sleep Essentials', text: 'Blackout curtains', required: false },
    { id: '8', category: 'Sleep Essentials', text: 'Night light (red/amber)', required: false },
    { id: '9', category: 'Changing Station', text: 'Changing pad', required: true },
    { id: '10', category: 'Changing Station', text: 'Diapers (newborn + size 1)', required: true },
    { id: '11', category: 'Changing Station', text: 'Wipes', required: true },
    { id: '12', category: 'Changing Station', text: 'Diaper cream', required: true },
    { id: '13', category: 'Changing Station', text: 'Diaper pail', required: false },
    { id: '14', category: 'Changing Station', text: 'Extra changing pad covers', required: false },
    { id: '15', category: 'Feeding Corner', text: 'Comfortable chair/glider', required: false },
    { id: '16', category: 'Feeding Corner', text: 'Side table for supplies', required: false },
    { id: '17', category: 'Feeding Corner', text: 'Nursing pillow', required: false },
    { id: '18', category: 'Feeding Corner', text: 'Burp cloths', required: true },
    { id: '19', category: 'Storage', text: 'Dresser for clothes', required: true },
    { id: '20', category: 'Storage', text: 'Closet organizer', required: false },
    { id: '21', category: 'Storage', text: 'Bins for toys/supplies', required: false },
    { id: '22', category: 'Safety', text: 'Outlet covers installed', required: true },
    { id: '23', category: 'Safety', text: 'Furniture anchored to walls', required: true },
    { id: '24', category: 'Safety', text: 'Cords out of reach', required: true },
    { id: '25', category: 'Safety', text: 'Smoke detector in room', required: true },
];

const NurseryChecklist: React.FC = () => {
    const [checked, setChecked] = React.useState<Set<string>>(() => {
        const saved = localStorage.getItem('coexpect_nursery');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });

    const toggle = (id: string) => {
        const next = new Set(checked);
        next.has(id) ? next.delete(id) : next.add(id);
        setChecked(next);
        localStorage.setItem('coexpect_nursery', JSON.stringify([...next]));
    };

    const categories = [...new Set(NURSERY_ITEMS.map(i => i.category))];
    const progress = Math.round((checked.size / NURSERY_ITEMS.length) * 100);

    return (
        <div className="h-full flex flex-col bg-stone-50 overflow-y-auto">
            <div className="bg-white px-5 py-5 border-b border-stone-100">
                <h1 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                    <Lamp className="text-yellow-600" /> Nursery Setup
                </h1>
                <div className="mt-3 bg-stone-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-yellow-500 h-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-stone-500 mt-1">{checked.size} of {NURSERY_ITEMS.length} complete</p>
            </div>
            <div className="p-4 space-y-5 pb-24">
                {categories.map(cat => (
                    <div key={cat}>
                        <h2 className="text-xs font-bold text-stone-400 uppercase mb-2">{cat}</h2>
                        <div className="space-y-2">
                            {NURSERY_ITEMS.filter(i => i.category === cat).map(item => (
                                <button key={item.id} onClick={() => toggle(item.id)} className={`w-full text-left p-3 rounded-xl border transition-all ${checked.has(item.id) ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-stone-100'}`}>
                                    <div className="flex gap-3">
                                        <div className={`mt-0.5 ${checked.has(item.id) ? 'text-yellow-500' : 'text-stone-300'}`}>
                                            {checked.has(item.id) ? <Check size={18} /> : <Circle size={18} />}
                                        </div>
                                        <div className="flex-1">
                                            <p className={`font-medium text-sm ${checked.has(item.id) ? 'text-stone-500 line-through' : 'text-stone-800'}`}>{item.text}</p>
                                        </div>
                                        {item.required && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-bold">MUST HAVE</span>}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// === EMERGENCY PREP CHECKLIST COMPONENT ===
const EMERGENCY_ITEMS = [
    { id: '1', category: 'Know the Signs', text: 'Water breaking (gush or trickle)', icon: '💧' },
    { id: '2', category: 'Know the Signs', text: 'Contractions 5 min apart, 1 min long, for 1 hour', icon: '⏱️' },
    { id: '3', category: 'Know the Signs', text: 'Heavy bleeding (more than spotting)', icon: '🚨' },
    { id: '4', category: 'Know the Signs', text: 'Baby not moving as usual', icon: '👶' },
    { id: '5', category: 'Know the Signs', text: 'Severe headache or vision changes', icon: '👁️' },
    { id: '6', category: 'Go-Bag Ready', text: 'Hospital bag packed and by door', icon: '🎒' },
    { id: '7', category: 'Go-Bag Ready', text: 'Car seat installed correctly', icon: '🚗' },
    { id: '8', category: 'Go-Bag Ready', text: 'Phone chargers in bag', icon: '🔌' },
    { id: '9', category: 'Go-Bag Ready', text: 'ID and insurance cards ready', icon: '💳' },
    { id: '10', category: 'Go-Bag Ready', text: 'Birth plan printed (2 copies)', icon: '📋' },
    { id: '11', category: 'Route Planned', text: 'Primary route to hospital saved', icon: '🗺️' },
    { id: '12', category: 'Route Planned', text: 'Backup route planned', icon: '🛣️' },
    { id: '13', category: 'Route Planned', text: 'Hospital address in phone', icon: '📍' },
    { id: '14', category: 'Route Planned', text: 'Parking/entrance info saved', icon: '🅿️' },
    { id: '15', category: 'Route Planned', text: 'Done a practice drive', icon: '✅' },
    { id: '16', category: 'Contact List', text: 'Doctor/midwife number saved', icon: '📞' },
    { id: '17', category: 'Contact List', text: 'Hospital L&D number saved', icon: '🏥' },
    { id: '18', category: 'Contact List', text: 'Emergency contact list ready', icon: '📱' },
    { id: '19', category: 'Contact List', text: 'Doula contact saved (if applicable)', icon: '🤱' },
    { id: '20', category: 'Home Prep', text: 'Pet care plan arranged', icon: '🐕' },
    { id: '21', category: 'Home Prep', text: 'Other kids care plan set', icon: '👧' },
    { id: '22', category: 'Home Prep', text: 'Work notified of leave plan', icon: '💼' },
    { id: '23', category: 'Home Prep', text: 'Freezer meals prepped', icon: '🍲' },
];

const EmergencyChecklist: React.FC = () => {
    const [checked, setChecked] = React.useState<Set<string>>(() => {
        const saved = localStorage.getItem('coexpect_emergency');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });

    const toggle = (id: string) => {
        const next = new Set(checked);
        next.has(id) ? next.delete(id) : next.add(id);
        setChecked(next);
        localStorage.setItem('coexpect_emergency', JSON.stringify([...next]));
    };

    const categories = [...new Set(EMERGENCY_ITEMS.map(i => i.category))];
    const progress = Math.round((checked.size / EMERGENCY_ITEMS.length) * 100);

    return (
        <div className="h-full flex flex-col bg-stone-50 overflow-y-auto">
            <div className="bg-gradient-to-br from-red-500 to-orange-500 px-5 py-6 text-white">
                <h1 className="text-xl font-bold flex items-center gap-2">
                    <AlertTriangle /> Emergency Prep
                </h1>
                <p className="text-red-100 text-sm mt-1">Be ready when it's go-time</p>
                <div className="mt-4 bg-white/20 h-2 rounded-full overflow-hidden">
                    <div className="bg-white h-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-white/80 mt-1">{checked.size} of {EMERGENCY_ITEMS.length} complete</p>
            </div>
            <div className="p-4 space-y-5 pb-24">
                {categories.map(cat => (
                    <div key={cat}>
                        <h2 className="text-xs font-bold text-stone-400 uppercase mb-2">{cat}</h2>
                        <div className="space-y-2">
                            {EMERGENCY_ITEMS.filter(i => i.category === cat).map(item => (
                                <button key={item.id} onClick={() => toggle(item.id)} className={`w-full text-left p-3 rounded-xl border transition-all ${checked.has(item.id) ? 'bg-green-50 border-green-200' : 'bg-white border-stone-100'}`}>
                                    <div className="flex gap-3 items-center">
                                        <span className="text-lg">{item.icon}</span>
                                        <p className={`flex-1 font-medium text-sm ${checked.has(item.id) ? 'text-stone-500 line-through' : 'text-stone-800'}`}>{item.text}</p>
                                        <div className={checked.has(item.id) ? 'text-green-500' : 'text-stone-300'}>
                                            {checked.has(item.id) ? <Check size={18} /> : <Circle size={18} />}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// === PROVIDER INFO COMPONENT ===
interface Provider {
    id: string;
    name: string;
    role: string;
    phone: string;
    address: string;
}

const ProviderInfo: React.FC<{ user: UserProfile }> = ({ user }) => {
    const [providers, setProviders] = React.useState<Provider[]>(() => {
        const saved = localStorage.getItem('coexpect_providers');
        return saved ? JSON.parse(saved) : [];
    });
    const [insurancePhoto, setInsurancePhoto] = React.useState<string | null>(() => {
        return localStorage.getItem('coexpect_insurance_photo');
    });
    const [adding, setAdding] = React.useState(false);
    const [form, setForm] = React.useState({ name: '', role: 'OB/GYN', phone: '', address: '' });

    const saveProviders = (list: Provider[]) => {
        setProviders(list);
        localStorage.setItem('coexpect_providers', JSON.stringify(list));
    };

    const addProvider = () => {
        if (!form.name) return;
        const newP: Provider = { id: Date.now().toString(), ...form };
        saveProviders([...providers, newP]);
        setForm({ name: '', role: 'OB/GYN', phone: '', address: '' });
        setAdding(false);
    };

    const removeProvider = (id: string) => {
        saveProviders(providers.filter(p => p.id !== id));
    };

    const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                const data = ev.target?.result as string;
                setInsurancePhoto(data);
                localStorage.setItem('coexpect_insurance_photo', data);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="h-full flex flex-col bg-stone-50 overflow-y-auto">
            <div className="bg-white px-5 py-5 border-b border-stone-100">
                <h1 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                    <Phone className="text-indigo-600" /> Care Team & Insurance
                </h1>
                <p className="text-stone-500 text-sm mt-1">Quick access when you need it</p>
            </div>
            <div className="p-4 space-y-5 pb-24">
                {/* Providers */}
                <div>
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-xs font-bold text-stone-400 uppercase">Healthcare Providers</h2>
                        <button onClick={() => setAdding(true)} className="text-indigo-600 text-xs font-bold flex items-center gap-1">
                            <Plus size={14} /> Add
                        </button>
                    </div>

                    {adding && (
                        <div className="bg-white p-4 rounded-xl border border-indigo-200 mb-3 space-y-3">
                            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full p-2 border rounded-lg text-sm" />
                            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full p-2 border rounded-lg text-sm bg-white">
                                <option>OB/GYN</option>
                                <option>Midwife</option>
                                <option>Doula</option>
                                <option>Pediatrician</option>
                                <option>Hospital</option>
                                <option>Lactation Consultant</option>
                                <option>Other</option>
                            </select>
                            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" type="tel" className="w-full p-2 border rounded-lg text-sm" />
                            <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address" className="w-full p-2 border rounded-lg text-sm" />
                            <div className="flex gap-2">
                                <button onClick={addProvider} className="flex-1 bg-indigo-600 text-white py-2 rounded-lg font-bold text-sm">Save</button>
                                <button onClick={() => setAdding(false)} className="flex-1 bg-stone-100 text-stone-600 py-2 rounded-lg font-bold text-sm">Cancel</button>
                            </div>
                        </div>
                    )}

                    {providers.length === 0 && !adding ? (
                        <div className="bg-white p-6 rounded-xl border border-dashed border-stone-200 text-center">
                            <User className="mx-auto text-stone-300 mb-2" size={32} />
                            <p className="text-stone-500 text-sm">Add your care team contacts</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {providers.map(p => (
                                <div key={p.id} className="bg-white p-4 rounded-xl border border-stone-100">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="font-bold text-stone-900">{p.name}</p>
                                            <p className="text-xs text-indigo-600 font-medium">{p.role}</p>
                                        </div>
                                        <button onClick={() => removeProvider(p.id)} className="text-stone-300 p-1"><Trash2 size={16} /></button>
                                    </div>
                                    {p.phone && (
                                        <a href={`tel:${p.phone}`} className="flex items-center gap-2 mt-2 text-sm text-stone-600 bg-stone-50 px-3 py-2 rounded-lg">
                                            <Phone size={14} /> {p.phone}
                                        </a>
                                    )}
                                    {p.address && (
                                        <a href={`https://maps.google.com/?q=${encodeURIComponent(p.address)}`} target="_blank" className="flex items-center gap-2 mt-2 text-sm text-stone-600 bg-stone-50 px-3 py-2 rounded-lg">
                                            <MapPin size={14} /> {p.address}
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Insurance Card */}
                <div>
                    <h2 className="text-xs font-bold text-stone-400 uppercase mb-3">Insurance Card</h2>
                    {insurancePhoto ? (
                        <div className="relative">
                            <img src={insurancePhoto} alt="Insurance Card" className="w-full rounded-xl border border-stone-200 shadow-sm" />
                            <button onClick={() => { setInsurancePhoto(null); localStorage.removeItem('coexpect_insurance_photo'); }} className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ) : (
                        <label className="block bg-white p-6 rounded-xl border border-dashed border-stone-200 text-center cursor-pointer hover:bg-stone-50 transition-colors">
                            <CreditCard className="mx-auto text-stone-300 mb-2" size={32} />
                            <p className="text-stone-500 text-sm">Tap to upload insurance card photo</p>
                            <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                        </label>
                    )}
                </div>
            </div>
        </div>
    );
};

interface ToolsProps {
    user: UserProfile;
    initialTool?: string;
    onNavigateToLaborMode?: () => void;
    onNavigateToSettings?: () => void;
}

type ToolView = 'HUB' | 'KICKS' | 'BAG' | 'FOOD' | 'NAMES' | 'DOC' | 'BOOTH' | 'PLAN' | 'RELAX' | 'SYMPTOMS' | 'CONTRACTIONS' | 'DUEDATE' | 'BABYSIZE' | 'CARSEAT' | 'NURSERY' | 'EMERGENCY' | 'PROVIDER' | 'REMINDERS' | 'FACTS' | 'BLOODPRESSURE' | 'WATER' | 'PELVIC' | 'FUNDAL' | 'PARTNERHUB' | 'MENTALHEALTH' | 'GLUCOSE';

// Map from string identifiers to ToolView
const toolNameMap: Record<string, ToolView> = {
    flashfacts: 'FACTS',
    kicks: 'KICKS',
    bag: 'BAG',
    food: 'FOOD',
    names: 'NAMES',
    doc: 'DOC',
    booth: 'BOOTH',
    plan: 'PLAN',
    relax: 'RELAX',
    zen: 'RELAX',
    soothe: 'RELAX',
    contractions: 'CONTRACTIONS',
    duedate: 'DUEDATE',
    babysize: 'BABYSIZE',
    carseat: 'CARSEAT',
    nursery: 'NURSERY',
    emergency: 'EMERGENCY',
    provider: 'PROVIDER',
    reminders: 'REMINDERS',
    symptoms: 'SYMPTOMS',
    bloodpressure: 'BLOODPRESSURE',
    water: 'WATER',
    pelvic: 'PELVIC',
    pelvicfloor: 'PELVIC',
    fundal: 'FUNDAL',
    fundalheight: 'FUNDAL',
    partnerhub: 'PARTNERHUB',
    partner: 'PARTNERHUB',
    mentalhealth: 'MENTALHEALTH',
    epds: 'MENTALHEALTH',
    mood: 'MENTALHEALTH',
    glucose: 'GLUCOSE',
    gdm: 'GLUCOSE',
    diabetes: 'GLUCOSE',
};

export const Tools: React.FC<ToolsProps> = ({ user, initialTool, onNavigateToLaborMode, onNavigateToSettings }) => {
    const [activeTool, setActiveTool] = useState<ToolView>(() => {
        if (initialTool && toolNameMap[initialTool.toLowerCase()]) {
            return toolNameMap[initialTool.toLowerCase()];
        }
        return 'HUB';
    });

    if (activeTool !== 'HUB') {
        let ToolComponent = null;
        switch (activeTool) {
            case 'KICKS': ToolComponent = <KickCounter />; break;
            case 'BAG': ToolComponent = <HospitalBag dueDate={user.dueDate} onNavigateToTool={(tool) => setActiveTool(toolNameMap[tool] || 'HUB')} />; break;
            case 'FOOD': ToolComponent = <FoodSafety />; break;
            case 'NAMES': ToolComponent = <NameMatcher user={user} />; break;
            case 'DOC': ToolComponent = <DocVault user={user} />; break;
            case 'BOOTH': ToolComponent = <BumpBooth user={user} />; break;
            case 'PLAN': ToolComponent = <BirthPlanBuilder />; break;
            case 'RELAX': ToolComponent = <RelaxMode />; break;
            case 'CONTRACTIONS': ToolComponent = <ContractionTimer onSwitchToLaborMode={onNavigateToLaborMode} />; break;
            case 'DUEDATE': ToolComponent = <DueDateCalculator />; break;
            case 'BABYSIZE': ToolComponent = <BabySizeVisualizer dueDate={user.dueDate} />; break;
            case 'CARSEAT': ToolComponent = <CarSeatChecklist />; break;
            case 'NURSERY': ToolComponent = <NurseryChecklist />; break;
            case 'EMERGENCY': ToolComponent = <EmergencyChecklist />; break;
            case 'PROVIDER': ToolComponent = <ProviderInfo user={user} />; break;
            case 'REMINDERS': ToolComponent = <Reminders user={user} />; break;
            case 'FACTS': ToolComponent = <FlashFactsViewer user={user} />; break;
            case 'SYMPTOMS': ToolComponent = <HealthLog user={user} initialTab="symptoms" onNavigateToTool={(tool) => setActiveTool(toolNameMap[tool] || 'HUB')} />; break;
            case 'BLOODPRESSURE': ToolComponent = <BloodPressureLog />; break;
            case 'WATER': ToolComponent = <WaterTracker />; break;
            case 'PELVIC': ToolComponent = <PelvicFloorTimer />; break;
            case 'FUNDAL': ToolComponent = <FundalHeightTracker user={user} />; break;
            case 'PARTNERHUB': ToolComponent = <PartnerHub user={user} />; break;
            case 'MENTALHEALTH': ToolComponent = <MentalHealthScreener />; break;
            case 'GLUCOSE': ToolComponent = <GlucoseTracker />; break;
        }

        return (
            <div className="h-full flex flex-col">
                <div className="bg-white px-4 pt-2 pb-1 border-b border-stone-50 z-20">
                    <button onClick={() => setActiveTool('HUB')} className="flex items-center gap-1 text-stone-500 font-medium text-sm p-2 hover:bg-stone-50 rounded-lg active:scale-95 transition-transform">
                        <ArrowLeft size={16} /> Back to Tools
                    </button>
                </div>
                <div className="flex-1 overflow-hidden relative">
                    <Suspense fallback={<ToolLoader />}>
                        {ToolComponent}
                    </Suspense>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-stone-50">
            {/* Header */}
            <div className="bg-white px-5 py-4 rounded-b-2xl shadow-sm border-b border-stone-100 sticky top-0 z-10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-stone-900 p-2.5 rounded-xl text-white">
                            <Wrench size={20} />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-stone-900">Tools</h1>
                            <p className="text-stone-500 text-xs">Everything for your journey</p>
                        </div>
                    </div>
                    {onNavigateToSettings && (
                        <button
                            onClick={onNavigateToSettings}
                            className="p-2.5 bg-stone-100 rounded-xl text-stone-600 hover:bg-stone-200 transition-colors"
                            aria-label="Settings"
                        >
                            <Settings size={20} />
                        </button>
                    )}
                </div>
            </div>

            {/* Tool Grid */}
            <div className="p-4 overflow-y-auto flex-1">
                <div className="grid grid-cols-2 gap-3 pb-20">

                    {/* === LABOR & BIRTH === */}
                    <SectionHeader title="Labor & Birth" />

                    <ToolCard
                        icon={Activity}
                        iconColor="rose"
                        title="Contraction Timer"
                        subtitle="Time contractions & know when to go"
                        onClick={() => setActiveTool('CONTRACTIONS')}
                    />

                    <ToolCard
                        icon={FileText}
                        iconColor="teal"
                        title="Birth Plan"
                        subtitle="Document your delivery preferences"
                        onClick={() => setActiveTool('PLAN')}
                    />

                    <ToolCard
                        icon={ShoppingBag}
                        iconColor="violet"
                        title="Hospital Bag"
                        subtitle="Checklist for mom, partner & baby"
                        onClick={() => setActiveTool('BAG')}
                    />

                    {/* === BABY & GROWTH === */}
                    <SectionHeader title="Baby & Growth" />

                    <ToolCard
                        icon={Baby}
                        iconColor="pink"
                        title="Baby Size"
                        subtitle="Visual size comparison by week"
                        onClick={() => setActiveTool('BABYSIZE')}
                    />

                    <ToolCard
                        icon={Calendar}
                        iconColor="purple"
                        title="Birth Stats"
                        subtitle="Due date probabilities & stats"
                        onClick={() => setActiveTool('DUEDATE')}
                    />

                    <ToolCard
                        icon={Footprints}
                        iconColor="orange"
                        title="Kick Counter"
                        subtitle="Track baby's daily movements"
                        onClick={() => setActiveTool('KICKS')}
                    />

                    {/* === HEALTH & TRACKING === */}
                    <SectionHeader title="Health & Tracking" />

                    <ToolCard
                        icon={Heart}
                        iconColor="violet"
                        title="Health Log"
                        subtitle="Track symptoms & weight"
                        onClick={() => setActiveTool('SYMPTOMS')}
                    />

                    <ToolCard
                        icon={Bell}
                        iconColor="amber"
                        title="Reminders"
                        subtitle="Vitamins, meds & hydration"
                        onClick={() => setActiveTool('REMINDERS')}
                    />

                    <ToolCard
                        icon={Brain}
                        iconColor="rose"
                        title="Mental Health"
                        subtitle="EPDS screening (ACOG 2025)"
                        onClick={() => setActiveTool('MENTALHEALTH')}
                    />

                    <ToolCard
                        icon={Droplets}
                        iconColor="violet"
                        title="Glucose Tracker"
                        subtitle="For gestational diabetes (ADA 2026)"
                        onClick={() => setActiveTool('GLUCOSE')}
                    />

                    <ToolCard
                        icon={Activity}
                        iconColor="rose"
                        title="Blood Pressure"
                        subtitle="Monitor for preeclampsia"
                        onClick={() => setActiveTool('BLOODPRESSURE')}
                    />

                    <ToolCard
                        icon={Droplets}
                        iconColor="blue"
                        title="Water Intake"
                        subtitle="Stay hydrated daily"
                        onClick={() => setActiveTool('WATER')}
                    />

                    <ToolCard
                        icon={Activity}
                        iconColor="violet"
                        title="Pelvic Floor"
                        subtitle="Guided Kegel exercises"
                        onClick={() => setActiveTool('PELVIC')}
                    />

                    <ToolCard
                        icon={Ruler}
                        iconColor="indigo"
                        title="Fundal Height"
                        subtitle="Track uterine growth"
                        onClick={() => setActiveTool('FUNDAL')}
                    />

                    <ToolCard
                        icon={Utensils}
                        iconColor="emerald"
                        title="Safe Eats"
                        subtitle="Check food safety for pregnancy"
                        onClick={() => setActiveTool('FOOD')}
                    />

                    <ToolCard
                        icon={Phone}
                        iconColor="indigo"
                        title="Care Team"
                        subtitle="Provider contacts & insurance"
                        onClick={() => setActiveTool('PROVIDER')}
                    />

                    <ToolCard
                        icon={Stethoscope}
                        iconColor="teal"
                        title="Doc Vault"
                        subtitle="Store records & documents"
                        onClick={() => setActiveTool('DOC')}
                    />

                    {/* === PREPARE & RELAX === */}
                    <SectionHeader title="Prepare & Relax" />

                    {/* Partner Hub - only show when linked */}
                    {user.isLinked && (
                        <ToolCard
                            icon={Users}
                            iconColor="emerald"
                            title="Partner Hub"
                            subtitle={`Shared tasks & calendar with ${user.linkedPartnerName || user.partnerName}`}
                            onClick={() => setActiveTool('PARTNERHUB')}
                        />
                    )}

                    <ToolCard
                        icon={Sparkles}
                        iconColor="amber"
                        title="FlashFacts"
                        subtitle="Learn 500+ pregnancy facts"
                        onClick={() => setActiveTool('FACTS')}
                    />

                    <ToolCard
                        icon={Heart}
                        iconColor="rose"
                        title="NameSync"
                        subtitle="Swipe with partner to match"
                        onClick={() => setActiveTool('NAMES')}
                    />

                    <ToolCard
                        icon={Wind}
                        iconColor="indigo"
                        title="Relax"
                        subtitle="Breathing exercises & sounds"
                        onClick={() => setActiveTool('RELAX')}
                    />

                    <ToolCard
                        icon={Camera}
                        iconColor="purple"
                        title="Bump Booth"
                        subtitle="Capture your bump journey"
                        onClick={() => setActiveTool('BOOTH')}
                    />

                    <ToolCard
                        icon={Car}
                        iconColor="green"
                        title="Car Seat"
                        subtitle="Installation checklist"
                        onClick={() => setActiveTool('CARSEAT')}
                    />

                    <ToolCard
                        icon={Lamp}
                        iconColor="yellow"
                        title="Nursery"
                        subtitle="Room setup checklist"
                        onClick={() => setActiveTool('NURSERY')}
                    />

                    <ToolCard
                        icon={AlertTriangle}
                        iconColor="red"
                        title="Emergency Prep"
                        subtitle="When to go to hospital"
                        onClick={() => setActiveTool('EMERGENCY')}
                    />


                </div>
            </div>
        </div>
    );
};
